<template>
  <section
    :class="['support-system', { 'is-flow': flow }]"
    :style="boxStyle"
    aria-labelledby="support-system-title"
  >
    <div class="support-system__inner">
      <h2 id="support-system-title" class="support-system__title">
        StoReel’s<br />
        Creator Support System
      </h2>
      <p class="support-system__subtitle">We are here to help your creativity thrive.</p>
      <p class="support-system__description">
        Whether you’re a solo creator, a small studio, or a filmmaker exploring new formats,
        StoReel provides end-to-end support — from story development to global reach.
      </p>

      <div class="support-system__grid">
        <article
          v-for="card in cards"
          :key="card.title"
          :class="['support-system__card', `is-${card.tone}`]"
        >
          <h3>{{ card.title }}</h3>
          <p>{{ card.description }}</p>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { CSSProperties } from 'vue'

import type { DesignBox } from '../../data/home2026'

const props = defineProps<{
  box: DesignBox
  cards: {
    title: string
    description: string
    tone: string
  }[]
  scaledPx: (value: number) => string
  flow?: boolean
}>()

const boxStyle = computed<CSSProperties>(() => ({
  ...(props.flow
    ? {
        width: '100%',
      }
    : {
        left: props.scaledPx(props.box.x),
        top: props.scaledPx(props.box.y),
        width: props.scaledPx(props.box.w),
        height: props.scaledPx(props.box.h),
      }),
  '--support-inner-width': props.scaledPx(1680),
  '--support-side-padding': props.scaledPx(200),
  '--support-title-font': props.scaledPx(70),
  '--support-title-line': props.scaledPx(98),
  '--support-subtitle-top': props.scaledPx(100),
  '--support-subtitle-font': props.scaledPx(50),
  '--support-subtitle-line': props.scaledPx(70),
  '--support-description-width': props.scaledPx(1012),
  '--support-description-top': props.scaledPx(50),
  '--support-description-font': props.scaledPx(30),
  '--support-description-line': props.scaledPx(42),
  '--support-grid-top': props.scaledPx(50),
  '--support-grid-row-gap': props.scaledPx(20),
  '--support-grid-column-gap': props.scaledPx(20),
  '--support-card-min-height': props.scaledPx(750),
  '--support-card-padding-y': props.scaledPx(100),
  '--support-card-padding-x': props.scaledPx(50),
  '--support-card-radius': props.scaledPx(30),
  '--support-card-title-font': props.scaledPx(50),
  '--support-card-title-line': props.scaledPx(70),
  '--support-card-copy-top': props.scaledPx(410),
  '--support-card-copy-font': props.scaledPx(30),
  '--support-card-copy-line': props.scaledPx(42),
  '--support-flow-bottom': props.scaledPx(200),
}))
</script>

<style scoped>
.support-system {
  position: absolute;
  z-index: 3;
  box-sizing: border-box;
  overflow: hidden;
  background: #000;
  color: #fff;
  font-family: 'Alibaba PuHuiTi 3.0', AlibabaPuHuiTi_3_55_Regular, PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-synthesis: none;
  -webkit-font-smoothing: antialiased;
  text-rendering: geometricprecision;
}

.support-system.is-flow {
  position: relative;
  left: auto;
  top: auto;
  height: auto;
  padding-bottom: var(--support-flow-bottom);
  overflow: visible;
}

.support-system__inner {
  width: min(var(--support-inner-width), calc(100% - var(--support-side-padding) * 2));
  margin: 0 auto;
  text-align: center;
}

.support-system__title {
  margin: 0;
  font-family: AlibabaPuHuiTi_3_115_Black, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--support-title-font);
  font-weight: 1000;
  line-height: var(--support-title-line);
  letter-spacing: 0;
}

.support-system__subtitle {
  margin: var(--support-subtitle-top) 0 0;
  font-family: AlibabaPuHuiTi_3_115_Black, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--support-subtitle-font);
  font-weight: 1000;
  line-height: var(--support-subtitle-line);
  letter-spacing: 0;
}

.support-system__description {
  width: min(var(--support-description-width), 100%);
  margin: var(--support-description-top) auto 0;
  color: rgb(255 255 255 / 78%);
  font-family: AlibabaPuHuiTi_3_55_Regular, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--support-description-font);
  font-weight: 400;
  line-height: var(--support-description-line);
  letter-spacing: 0;
}

.support-system__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--support-grid-row-gap) var(--support-grid-column-gap);
  margin-top: var(--support-grid-top);
}

.support-system__card {
  box-sizing: border-box;
  min-height: var(--support-card-min-height);
  padding: var(--support-card-padding-y) var(--support-card-padding-x);
  overflow: hidden;
  border-radius: var(--support-card-radius);
  background: #000;
  color: #000;
  text-align: left;
}

.support-system__card.is-blue {
  background: #89b6f3;
}

.support-system__card.is-lavender {
  background: #c1b3ed;
}

.support-system__card h3 {
  margin: 0;
  font-family: AlibabaPuHuiTi_3_65_Medium, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--support-card-title-font);
  font-weight: 500;
  line-height: var(--support-card-title-line);
  letter-spacing: 0;
}

.support-system__card p {
  margin: var(--support-card-copy-top) 0 0;
  font-family: AlibabaPuHuiTi_3_55_Regular, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-size: var(--support-card-copy-font);
  font-weight: 400;
  line-height: var(--support-card-copy-line);
  letter-spacing: 0;
}
</style>
